/**
 * RecipientSelector Component
 *
 * Inline "To:" field with connection search and multi-select for iMessage-style
 * conversation creation. Supports autocomplete, keyboard navigation, and
 * existing conversation detection.
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Search, X, MessageSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useConnections, useConnectionLoading, useConnectionActions } from '@/stores/connection';
import { useConversations } from '@/stores/messaging';
import { SUCCESS_GREEN } from '@/styles/tokens';
import type { ConversationParticipant, Conversation } from '@/types/messaging';
import type { UserConnection } from '@/types/connections';

interface RecipientSelectorProps {
  recipients: ConversationParticipant[];
  onAddRecipient: (recipient: ConversationParticipant) => void;
  onRemoveRecipient: (userId: string) => void;
  onOpenExisting?: (conversation: Conversation) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (user: { firstName?: string; lastName?: string; username: string }): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * RecipientChip - Selected recipient display with remove button
 */
interface RecipientChipProps {
  recipient: ConversationParticipant;
  onRemove: () => void;
  disabled?: boolean;
}

const RecipientChip: React.FC<RecipientChipProps> = ({ recipient, onRemove, disabled }) => {
  const displayName = getDisplayName(recipient);

  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors bg-primary/10 text-primary">
      <Avatar className="h-5 w-5">
        <AvatarImage src={recipient.avatar} alt={displayName} />
        <AvatarFallback
          className="border border-primary/20 bg-primary/10 text-[10px] font-semibold text-primary"
        >
          {getInitials(recipient.firstName, recipient.lastName, recipient.username)}
        </AvatarFallback>
      </Avatar>
      <span className="font-medium max-w-[120px] truncate">{displayName}</span>
      {!disabled && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 p-0.5 rounded-full hover:bg-primary/20 transition-colors"
          aria-label={`Remove ${displayName}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

/**
 * ConnectionSuggestion - Single connection in dropdown
 */
interface ConnectionSuggestionProps {
  connection: UserConnection;
  existingConversation?: Conversation | null;
  isHighlighted: boolean;
  onClick: () => void;
}

const ConnectionSuggestion: React.FC<ConnectionSuggestionProps> = ({
  connection,
  existingConversation,
  isHighlighted,
  onClick,
}) => {
  const displayName = getDisplayName(connection.user);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <button
      onClick={onClick}
      className={`w-full px-3 py-2 flex items-center gap-3 text-left transition-colors ${
        isHighlighted ? 'bg-accent' : 'hover:bg-accent/50'
      }`}
    >
      <div className="relative">
        <Avatar className="h-8 w-8">
          <AvatarImage src={connection.user.avatar} alt={displayName} />
          <AvatarFallback
            className="border border-primary/20 bg-primary/10 text-xs font-semibold text-primary"
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {connection.user.isOnline && (
          <span
            className="absolute bottom-0 right-0 h-2 w-2 rounded-full border border-card"
            style={{ backgroundColor: SUCCESS_GREEN.DEFAULT }}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
        <p className="text-xs text-muted-foreground">@{connection.user.username}</p>
      </div>

      {existingConversation && (
        <Badge className="text-xs px-2 py-0.5 flex items-center gap-1 bg-primary/10 text-primary border-0">
          <MessageSquare className="h-3 w-3" />
          Existing
        </Badge>
      )}
    </button>
  );
};

export const RecipientSelector: React.FC<RecipientSelectorProps> = ({
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  onOpenExisting,
  disabled = false,
  autoFocus = false,
  placeholder = 'Search connections...',
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get connections and existing conversations
  const connections = useConnections();
  const { isLoading: isLoadingConnections } = useConnectionLoading();
  const { fetchConnections } = useConnectionActions();
  const conversations = useConversations();

  // Fetch connections on mount
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Get selected recipient IDs for filtering
  const selectedIds = useMemo(
    () => new Set(recipients.map((r) => r.id)),
    [recipients]
  );

  // Find existing conversation for a user ID (exclude archived)
  const findExistingConversationForUser = useCallback(
    (userId: string): Conversation | null => {
      return (
        conversations.find((c) => !c.isGroup && !c.archivedAt && c.participant?.id === userId) ?? null
      );
    },
    [conversations]
  );

  // Filter connections based on search and already selected
  const filteredConnections = useMemo(() => {
    const searchLower = searchValue.toLowerCase().trim();

    return connections.filter((connection) => {
      // Exclude already selected
      if (selectedIds.has(connection.user.id)) {
        return false;
      }

      // Filter by search term
      if (searchLower) {
        const fullName = `${connection.user.firstName ?? ''} ${connection.user.lastName ?? ''}`.toLowerCase();
        const username = connection.user.username.toLowerCase();
        return fullName.includes(searchLower) || username.includes(searchLower);
      }

      return true;
    });
  }, [connections, searchValue, selectedIds]);

  // Reset highlighted index when filtered results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredConnections.length]);

  // Handle selecting a connection
  const handleSelectConnection = useCallback(
    (connection: UserConnection) => {
      // Check for existing conversation
      const existingConversation = findExistingConversationForUser(connection.user.id);

      if (existingConversation && onOpenExisting) {
        // If there's an existing conversation and no other recipients, offer to open it
        if (recipients.length === 0) {
          onOpenExisting(existingConversation);
          setSearchValue('');
          setIsDropdownOpen(false);
          return;
        }
      }

      // Add recipient
      const participant: ConversationParticipant = {
        id: connection.user.id,
        username: connection.user.username,
        firstName: connection.user.firstName ?? '',
        lastName: connection.user.lastName ?? '',
        avatar: connection.user.avatar,
        isOnline: connection.user.isOnline,
      };

      onAddRecipient(participant);
      setSearchValue('');
      setIsDropdownOpen(false);
      inputRef.current?.focus();
    },
    [findExistingConversationForUser, onOpenExisting, onAddRecipient, recipients.length]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isDropdownOpen || filteredConnections.length === 0) {
        // Handle backspace to remove last recipient when input is empty
        if (e.key === 'Backspace' && searchValue === '' && recipients.length > 0) {
          onRemoveRecipient(recipients[recipients.length - 1].id);
          return;
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filteredConnections.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredConnections[highlightedIndex]) {
            handleSelectConnection(filteredConnections[highlightedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsDropdownOpen(false);
          break;
      }
    },
    [
      isDropdownOpen,
      filteredConnections,
      highlightedIndex,
      handleSelectConnection,
      searchValue,
      recipients,
      onRemoveRecipient,
    ]
  );

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setIsDropdownOpen(true);
  }, []);

  // Handle input focus
  const handleInputFocus = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-focus on mount if specified
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  return (
    <div className="relative flex-1">
      {/* Selected recipients and input */}
      <div className="flex flex-wrap items-center gap-1.5 min-h-[40px]">
        {/* Selected recipient chips */}
        {recipients.map((recipient) => (
          <RecipientChip
            key={recipient.id}
            recipient={recipient}
            onRemove={() => onRemoveRecipient(recipient.id)}
            disabled={disabled}
          />
        ))}

        {/* Search input */}
        <div className="relative flex-1 min-w-[120px]">
          <Input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={recipients.length === 0 ? placeholder : 'Add more...'}
            disabled={disabled}
            className="border-0 shadow-none focus-visible:ring-0 px-1 h-8 text-sm"
          />
        </div>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-popover rounded-lg border border-border shadow-lg z-50 max-h-[280px] overflow-y-auto"
        >
          {isLoadingConnections ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Loading connections...
            </div>
          ) : filteredConnections.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {connections.length === 0
                ? 'No connections yet'
                : searchValue
                ? 'No matching connections'
                : 'All connections selected'}
            </div>
          ) : (
            <div className="py-1">
              {filteredConnections.map((connection, index) => (
                <ConnectionSuggestion
                  key={connection.user.id}
                  connection={connection}
                  existingConversation={findExistingConversationForUser(connection.user.id)}
                  isHighlighted={index === highlightedIndex}
                  onClick={() => handleSelectConnection(connection)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientSelector;
